import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { formatDateDMY } from '../utils/dateUtils';

// Client dashboard: an online loan staff sent back ("Awaiting Client") needs
// the client to review the changes / note and resubmit. Shown at the top,
// with the staff note, so it cannot be missed.
const SentBackBanner = ({ loans = [], onReview }) => {
  // The API marks a loan the client must review and resubmit (loan.state).
  const loan = (loans || []).find((l) => l.state?.actions?.resubmit === true);
  const [note, setNote] = useState(null);

  useEffect(() => {
    if (!loan?.id) { setNote(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(apiUrl(`/api/loans/${loan.id}/notes`), {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        // The API leaves internal notes out for clients.
        const latest = (Array.isArray(res.data) ? res.data : []).find((n) => String(n.note_text || '').trim());
        if (!cancelled) setNote(latest || null);
      } catch { /* the banner still shows without the note */ }
    })();
    return () => { cancelled = true; };
  }, [loan?.id]);

  if (!loan) return null;

  return (
    <div role="alert" className="mb-4 rounded-xl border-2 border-amber-400 dark:border-amber-500/70 bg-amber-50 dark:bg-amber-900/20 p-4 shadow-sm">
      <div className="flex flex-wrap items-start gap-3">
        <span className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/50">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-300/60 dark:bg-amber-600/40" />
          <AlertTriangle className="relative text-amber-600 dark:text-amber-400" size={22} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-amber-900 dark:text-amber-200">
            Action needed: your loan {loan.loan_code || ''} was sent back to you
          </p>
          <p className="mt-0.5 text-xs text-amber-800 dark:text-amber-300">
            Our staff reviewed your application and need you to look at it again. Please read their note, check the
            changes, and resubmit — we can't continue until you do.
          </p>
          {note && (
            <blockquote className="mt-2 rounded-lg border-l-4 border-amber-500 bg-white/70 dark:bg-slate-900/40 px-3 py-2 text-xs text-gray-800 dark:text-slate-200">
              <span className="block text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                Note from staff{note.created_at ? ` · ${formatDateDMY(note.created_at)}` : ''}
              </span>
              {note.note_text}
            </blockquote>
          )}
        </div>
        {typeof onReview === 'function' && (
          <button
            type="button"
            onClick={() => onReview(loan)}
            className="inline-flex flex-shrink-0 items-center gap-1.5 self-center rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-amber-700"
          >
            Review and resubmit <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

export default SentBackBanner;
